import { useRef } from 'react';
import gettext from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Modal from '@sdk/components/ui/modal/Modal';
import Text14 from '@sdk/components/ui/typography/Text14';
import Text12 from '@sdk/components/ui/typography/Text12';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import ModalFooterActionButtons from '@sdk/components/ui/modal/ModalFooterActionButtons';
import ModalBodyWithHeader from '@sdk/components/ui/modal/ModalBodyWithHeader';
import { SUBSCRIPTION_NAMES } from '@sdk/account/constants';
import getNowDate from '@sdk/utils/getNowDate';

import PauseBlueSvg from '@web/assets/svg/Icons/32/pause-blue.svg';
import { useBillingInfo } from '@web/account/billing/BillingInfoContext';
import DatePickerField from '@web/forms/components/datePicker/DatePickerField/DatePickerField';
import { DatePickerFieldWrapper } from '@web/account/billing/PauseSubscriptionModal/pauseSubscriptionModal.styles';
import { OutputDateFormatType } from '@web/forms/components/datePicker/datePicker.types';
import DateGraySvg from '@web/assets/svg/Icons/32/date-gray.svg';
import DowngradeReason, {
  DowngradeMode,
} from '@web/account/billing/DowngradeReason/DowngradeReason';

import { usePauseSubscriptionModal } from './pauseSubscriptionModal.hooks';

type Props = {
  isVisible: boolean;
  accept: () => void;
  decline: () => void;
  fromCancel?: boolean;
  fromDeleteOrg?: boolean;
};

const PauseSubscriptionModal = ({
  isVisible,
  accept,
  decline,
  fromCancel = false,
  fromDeleteOrg = false,
}: Props) => {
  const { billingInfo } = useBillingInfo();
  const subscriptionLevel = billingInfo.scheduledToResumeDate
    ? billingInfo.pausedSubscriptionLevel
    : billingInfo.subscriptionLevel;

  const config = useRef({ noDismiss: true }).current;
  const {
    acceptPauseSubscription,
    resumeDate,
    setResumeDate,
    askForReason,
    setAskForReason,
    selectedReasonId,
    setSelectedReasonId,
    selectedReasonText,
    setSelectedReasonText,
    reasonRequiresText,
    setReasonRequiresText,
    description,
  } = usePauseSubscriptionModal({
    accept,
    isVisible,
    subscriptionLevel,
    fromCancel,
  });

  const todayDate = getNowDate();

  const isSubmitActive = () => {
    if (askForReason) {
      return Boolean(selectedReasonId && (!reasonRequiresText || selectedReasonText));
    }

    return Boolean(resumeDate && resumeDate.getTime() > todayDate.getTime());
  };

  return (
    <>
      <Modal fixed config={config} isVisible={isVisible}>
        <ModalBodyWithHeader
          width={620}
          titleThemeColor={ThemeColorType.primary}
          titleText={
            billingInfo.scheduledToResumeDate
              ? gettext('Update the scheduled reactivation')
              : gettext('Pause subscription')
          }
          titleIcon={<PauseBlueSvg />}
          close={decline}>
          {askForReason ? (
            <Column mBottom={10}>
              <DowngradeReason
                selectedReasonId={selectedReasonId}
                setSelectedReasonId={setSelectedReasonId}
                selectedReasonText={selectedReasonText}
                setSelectedReasonText={setSelectedReasonText}
                setReasonRequiresText={setReasonRequiresText}
                mode={DowngradeMode.pause}
              />
            </Column>
          ) : (
            <>
              <Column mBottom={20}>
                <Text14 themeColor={ThemeColorType.gray}>{description}</Text14>
              </Column>
              <Row mBottom={20} mLeft={15} alignItems="center">
                <Text12 bold uppercase themeColor={ThemeColorType.gray}>
                  {gettext('Resume date')}
                </Text12>
                <Column pLeft={10} pRight={10}>
                  <DateGraySvg />
                </Column>
                <DatePickerFieldWrapper>
                  <DatePickerField
                    outputFormat={OutputDateFormatType.ddMMMyyyy}
                    placeholder={gettext('Select date')}
                    date={resumeDate}
                    customWidth={430}
                    minDate={todayDate}
                    onDateChange={(date: Date): void => {
                      setResumeDate(date);
                    }}
                  />
                </DatePickerFieldWrapper>
              </Row>
            </>
          )}
          <ModalFooterActionButtons
            mTop={0}
            buttonWidth={205}
            cancel={() => {
              if (askForReason) {
                return void setAskForReason(false);
              }

              return decline();
            }}
            submit={() => {
              if (!fromCancel && !fromDeleteOrg && !askForReason) {
                return void setAskForReason(true);
              }

              return acceptPauseSubscription(selectedReasonId, selectedReasonText);
            }}
            isSubmitActive={isSubmitActive()}
            isSubmitAsync={fromCancel || fromDeleteOrg || askForReason}
            cancelText={gettext('Nevermind')}
            submitText={
              billingInfo.scheduledToResumeDate
                ? gettext('Save')
                : gettext('Pause %(subscriptionName)s', {
                    subscriptionName: SUBSCRIPTION_NAMES[subscriptionLevel],
                  })
            }
          />
        </ModalBodyWithHeader>
      </Modal>
    </>
  );
};

export default PauseSubscriptionModal;
